import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useCallAnalytics = (timeRange: '7d' | '30d' | '90d' = '30d') => {
  return useQuery({
    queryKey: ['call-analytics', timeRange],
    queryFn: async () => {
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: calls, error } = await supabase
        .from('calie_calls')
        .select('*')
        .gte('started_at', startDate.toISOString());

      if (error) throw error;

      const totalCalls = calls?.length || 0;
      const completedCalls = calls?.filter(c => c.status === 'completed') || [];
      const avgDuration = completedCalls.length > 0 
        ? Math.round(completedCalls.reduce((sum, c) => sum + (c.duration_sec || 0), 0) / completedCalls.length)
        : 0;
      
      const sentimentScores = calls?.filter(c => c.sentiment_score !== null).map(c => c.sentiment_score) || [];
      const avgSentiment = sentimentScores.length > 0 
        ? sentimentScores.reduce((sum, score) => sum + score, 0) / sentimentScores.length
        : null;

      const answerRate = totalCalls > 0 ? (completedCalls.length / totalCalls) * 100 : 0;

      return {
        totalCalls,
        avgDuration,
        avgSentiment,
        answerRate,
        totalAnsweredCalls: completedCalls.length
      };
    },
    refetchInterval: 30000 // Refetch every 30 seconds
  });
};

export const useLiveCallsMonitoring = () => {
  return useQuery({
    queryKey: ['live-calls-monitoring'],
    queryFn: async () => {
      const { data: activeCalls, error } = await supabase
        .from('calie_calls')
        .select('*')
        .in('status', ['ringing', 'in-progress'])
        .order('started_at', { ascending: false });

      if (error) throw error;

      return activeCalls || [];
    },
    refetchInterval: 5000 // Refetch every 5 seconds for live monitoring
  });
};